/**
 * ConsumerApiClient — platform-agnostic HTTP transport for Pelipost consumer APIs.
 *
 * Two base URLs are supported because the backend exposes two surfaces the
 * consumer apps actually use:
 *   - mobileBaseUrl:  {apiUrl}/mobile   — the mobile/consumer-scoped endpoints
 *                                         (auth, cart, orders, products, …)
 *   - rootBaseUrl:    {apiUrl}          — the handful of shared endpoints not
 *                                         nested under /mobile (e.g. /support/*)
 *
 * Storage + "unauthorized" + "tokens refreshed" hooks are injected so this
 * package has no knowledge of AsyncStorage, SecureStore, cookies, or any
 * other host-specific storage mechanism.
 */

export interface ApiError {
  message: string;
  statusCode: number;
  error?: string;
}

export interface ConsumerApiClientConfig {
  /** Base URL for the mobile-scoped API, e.g. "https://dev-api.pelipost.com/api/v1/mobile". */
  mobileBaseUrl: string;

  /**
   * Base URL for root-level API calls that don't live under /mobile,
   * e.g. "https://dev-api.pelipost.com/api/v1". Optional — only needed
   * by modules that call root endpoints (currently: support).
   */
  rootBaseUrl?: string;

  /** Returns the current access token (or null/undefined when unauthenticated). */
  getToken: () => Promise<string | null | undefined>;

  /** Returns the current refresh token (or null/undefined). */
  getRefreshToken: () => Promise<string | null | undefined>;

  /** Persist a newly-minted access token. */
  setToken: (token: string) => Promise<void>;

  /** Persist a newly-minted refresh token. */
  setRefreshToken: (token: string) => Promise<void>;

  /** Clear all auth state — called when the refresh flow fails. */
  clearAuth: () => Promise<void>;

  /**
   * Called after a successful token refresh. Hosts can use this to sync
   * the new token into secondary storage (e.g. mobile's iOS App Group
   * SharedStorage, a service worker cache, etc.). Optional.
   */
  onTokensRefreshed?: (tokens: { accessToken: string; refreshToken?: string }) => void | Promise<void>;

  /**
   * Called when a request fails with 401 AND the refresh flow also fails
   * (or there is no refresh token). Hosts typically route to a login screen.
   * Optional — if absent, the caller receives the 401 error.
   */
  onUnauthorized?: () => void | Promise<void>;
}

export interface RequestOptions extends RequestInit {
  /** When true, no Authorization header is added and 401 → refresh is skipped. */
  skipAuth?: boolean;
}

/**
 * Normalises NestJS validation error shapes (which may have `message: string[]`)
 * into a single human-readable string.
 */
function coerceMessage(raw: unknown, fallback: string): string {
  if (raw && typeof raw === 'object' && 'message' in raw) {
    const m = (raw as { message: unknown }).message;
    if (Array.isArray(m)) return m.join('. ');
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return fallback;
}

export class ConsumerApiClient {
  constructor(private readonly config: ConsumerApiClientConfig) {}

  // ── Transport ─────────────────────────────────────────────────

  /**
   * Fire a request against the mobile-scoped base URL.
   * Endpoints are written relative (e.g. "/auth/login").
   */
  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return this.requestAbs<T>(`${this.config.mobileBaseUrl}${endpoint}`, options);
  }

  /**
   * Fire a request against the root (non-mobile) base URL.
   * Used by modules that hit endpoints outside /mobile/* (e.g. /support).
   * Throws if rootBaseUrl was not configured.
   */
  async requestRoot<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    if (!this.config.rootBaseUrl) {
      throw new Error(
        'ConsumerApiClient: rootBaseUrl is not configured but a root-scoped request was made. ' +
        'Pass rootBaseUrl in the client config to enable root endpoints (e.g. /support/*).',
      );
    }
    return this.requestAbs<T>(`${this.config.rootBaseUrl}${endpoint}`, options);
  }

  private async requestAbs<T>(url: string, options: RequestOptions = {}): Promise<T> {
    const { skipAuth, ...fetchOptions } = options;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(fetchOptions.headers as Record<string, string> | undefined),
    };

    if (!skipAuth) {
      const token = await this.config.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const response = await fetch(url, { ...fetchOptions, headers });

    if (response.status === 401 && !skipAuth) {
      const refreshed = await this.tryRefresh();
      if (refreshed) {
        const token = await this.config.getToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const retry = await fetch(url, { ...fetchOptions, headers });
        if (!retry.ok) {
          const err = await retry.json().catch(() => ({ message: 'Request failed' }));
          throw { message: coerceMessage(err, 'Request failed'), statusCode: retry.status } as ApiError;
        }
        if (retry.status === 204) return undefined as T;
        return retry.json() as Promise<T>;
      }
      await this.config.onUnauthorized?.();
      throw { message: 'Session expired', statusCode: 401 } as ApiError;
    }

    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Request failed' }));
      throw { message: coerceMessage(err, 'Request failed'), statusCode: response.status } as ApiError;
    }

    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  }

  private async tryRefresh(): Promise<boolean> {
    try {
      const refreshToken = await this.config.getRefreshToken();
      if (!refreshToken) {
        return false;
      }

      const response = await fetch(`${this.config.mobileBaseUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        await this.config.clearAuth();
        return false;
      }

      const data = (await response.json()) as { accessToken: string; refreshToken?: string };
      await this.config.setToken(data.accessToken);
      if (data.refreshToken) {
        await this.config.setRefreshToken(data.refreshToken);
      }
      await this.config.onTokensRefreshed?.({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      });
      return true;
    } catch {
      await this.config.clearAuth();
      return false;
    }
  }

  // ── Verb helpers (mobile-scoped) ──────────────────────────────

  get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  post<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  put<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  patch<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  delete<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  // ── Verb helpers (root-scoped) ────────────────────────────────

  getRoot<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.requestRoot<T>(endpoint, { ...options, method: 'GET' });
  }

  postRoot<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.requestRoot<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  // ── Upload (multipart/form-data) ──────────────────────────────

  /**
   * Upload a FormData payload to a mobile-scoped endpoint.
   * Host is responsible for constructing the FormData (which differs
   * between React Native and browsers).
   */
  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    const token = await this.config.getToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${this.config.mobileBaseUrl}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw { message: coerceMessage(err, 'Upload failed'), statusCode: response.status } as ApiError;
    }

    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  }
}
